import React, { useState, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import WelcomeOverlay from './WelcomeOverlay.tsx';
import VideoSection from './VideoSection.tsx';
import MainContent from './MainContent.tsx';
import QuickActions from './QuickActions.tsx';
import AutoScrollManager from './AutoScrollManager.tsx';
import MetaTags from './MetaTags.tsx';
import { useVideoPlayer } from '../hooks/useVideoPlayer.ts';
import { getColorScheme } from '../constants/colors.ts';
import { CardConfig } from '../constants/cards.ts';
import '../App.css';

interface WeddingCardProps {
  cardConfig: CardConfig;
}

const WeddingCard: React.FC<WeddingCardProps> = ({ cardConfig }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const rsvpSectionRef = useRef<HTMLElement>(null);
  
  // Get card-specific color scheme
  const colors = getColorScheme(cardConfig.id);
  
  const {
    videoEnded,
    videoError,
    videoRef,
    loopVideoRef,
    handleOpenInvitation: handleVideoOpenInvitation
  } = useVideoPlayer({
    audioRef,
    onShowQuickActions: () => setShowQuickActions(true),
    onContentShow: () => {},
    onStartAutoScroll: () => setIsAutoScrolling(true)
  });

  const handleOpenInvitation = async () => {
    setOverlayOpen(true);
    await handleVideoOpenInvitation();
  };

  const toggleMusic = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(error => console.error('Error playing audio:', error));
      }
    }
  };

  const scrollToRSVP = () => {
    rsvpSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen overflow-auto" style={{ backgroundColor: colors.secondary }}>
      {/* Dynamic Meta Tags */}
      <MetaTags cardConfig={cardConfig} />
      
      <AnimatePresence>
        {!overlayOpen && (
          <WelcomeOverlay 
            onOpenInvitation={handleOpenInvitation}
            cardConfig={cardConfig}
            colors={colors}
          />
        )}
      </AnimatePresence>

      {/* Background Music - card-specific */}
      <audio 
        ref={audioRef}
        loop
        src={cardConfig.audioPath}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      <AutoScrollManager
        isActive={isAutoScrolling}
        showQuickActions={showQuickActions}
        onAutoScrollStart={() => setIsAutoScrolling(true)}
        onAutoScrollStop={() => setIsAutoScrolling(false)}
      />

      <div className="w-full max-w-[430px] mx-auto relative">
        {/* Video Section - card-specific video */}
        <VideoSection
          showContent={true}
          videoEnded={videoEnded}
          videoError={videoError}
          videoRef={videoRef}
          loopVideoRef={loopVideoRef}
          onVideoEnded={() => {}}
          onVideoError={() => {}}
          onVideoLoaded={() => {}}
          videoPath={cardConfig.videoPath}
        />

        {/* Content - always visible and scrollable */}
        <div className="relative z-20" style={{ backgroundColor: colors.secondary }}>
          <MainContent 
            rsvpSectionRef={rsvpSectionRef}
            cardConfig={cardConfig}
            colors={colors}
          />
        </div>
      </div>

      {/* Quick Actions - show after 5 seconds */}
      {showQuickActions && (
        <QuickActions 
          isPlaying={isPlaying}
          toggleMusic={toggleMusic}
          scrollToRSVP={scrollToRSVP}
          colors={colors}
        />
      )}
    </div>
  );
};

export default WeddingCard;

